import React, { useEffect, useRef, useState } from 'react';
import SoundManager from '../utils/SoundManager';

/**
 * ALIEN — MU/TH/UR 6000 interface ("MOTHER"), the Nostromo's mainframe.
 * A self-contained amber-phosphor console in the spirit of the Amber Console
 * design system (github.com/DutchDiederik/AmberConsole). Pure DOM/CSS, no
 * external deps, no WebGL — all styles are scoped under `.alien-console`
 * so nothing leaks into the green C64 theme.
 */

interface Line {
  who: 'mother' | 'operator' | 'sys';
  text: string;
}

const BOOT_LINES = [
  'WEYLAND-YUTANI CORP // BUILD 2037',
  'MU/TH/UR 6000 MAINFRAME — ONLINE',
  'COMMERCIAL TOWING VEHICLE  USCSS NOSTROMO  180924609',
  'REG. CREW 7 // CARGO 20,000,000 TONS REFINED ORE',
  '',
  'LOADING NEUROMORPHIC CORE .............. OK',
  'LIFE SUPPORT ......................... NOMINAL',
  'HYPERSLEEP VAULT ................ 5 OCCUPANTS',
  'NAVIGATION LOCK ...................... ENGAGED',
  'SPECIAL ORDER 937 .............. [ RESTRICTED ]',
  '',
  'INTERFACE 2037 READY FOR INQUIRY',
];

// System indicator lights (decorative right rail)
const LIGHTS = [
  'REACT', 'O2', 'CO2', 'NAV', 'COMMS', 'CRYO',
  'GRAV', 'HULL', 'FUSE', 'AIRLK', 'THRM', 'AUX',
];

function motherReply(raw: string): Line[] {
  const q = raw.trim().toLowerCase();
  const m = (text: string): Line => ({ who: 'mother', text });

  if (!q) return [];

  // ---- Special Order 937 — the reveal ----
  if (
    q.includes('937') ||
    q.includes('special order') ||
    q === 'what is the story mother' ||
    q === "what's the story mother" ||
    q.includes('story mother') ||
    q.includes('order 937')
  ) {
    return [
      m('ACCESSING SPECIAL ORDER 937 ......... SCIENCE OFFICER EYES ONLY'),
      m('CLEARANCE OVERRIDE ACCEPTED.'),
      m(''),
      m('  PRIORITY ONE'),
      m('  INSURE RETURN OF ORGANISM FOR ANALYSIS.'),
      m('  ALL OTHER CONSIDERATIONS SECONDARY.'),
      m('  CREW EXPENDABLE.'),
    ];
  }

  if (q.includes('destruct') || q.includes('self-destruct') || q.includes('blow')) {
    return [{ who: 'sys', text: '__DESTRUCT__' }]; // handled by caller
  }

  if (q.includes('help') || q === '?' || q.includes('command') || q.includes('menu')) {
    return [
      m('INQUIRY CHANNELS AVAILABLE:'),
      m('  STATUS   — SHIP SYSTEMS'),
      m('  CREW     — PERSONNEL MANIFEST'),
      m('  ORGANISM — XENOMORPH ANALYSIS'),
      m('  ASH      — SCIENCE OFFICER'),
      m('  REPORT   — SPECIAL ORDER 937'),
      m('  DESTRUCT — EMERGENCY PROTOCOL'),
    ];
  }

  if (q.includes('status') || q.includes('report ship') || q === 'ship') {
    return [
      m('SHIP STATUS — USCSS NOSTROMO'),
      m('  LIFE SUPPORT ............ NOMINAL'),
      m('  ATMOSPHERE .......... 21% O2 / STABLE'),
      m('  REACTOR ................ 96.4% OUTPUT'),
      m('  HYPERSLEEP ....... 5 OF 7 IN STASIS'),
      m('  CURRENT HEADING ...... SOL / EARTH'),
      m('  UNSCHEDULED STOP ..... SYSTEM ZETA2'),
    ];
  }

  if (q.includes('crew') || q.includes('manifest') || q.includes('personnel')) {
    return [
      m('CREW MANIFEST — 7 SOULS'),
      m('  DALLAS    CAPTAIN ......... [ NO SIGNAL ]'),
      m('  KANE      EXEC ........... [ DECEASED ]'),
      m('  RIPLEY    WARRANT OFFICER . ACTIVE'),
      m('  LAMBERT   NAVIGATOR ....... ACTIVE'),
      m('  ASH       SCIENCE ......... ACTIVE'),
      m('  PARKER    ENGINEER ........ ACTIVE'),
      m('  BRETT     ENGINEER ........ [ NO SIGNAL ]'),
      m('  + 1 UNREGISTERED LIFEFORM ABOARD'),
    ];
  }

  if (q.includes('organism') || q.includes('alien') || q.includes('xeno') || q.includes('creature') || q.includes('analysis')) {
    return [
      m('ORGANISM — CLASSIFICATION PENDING'),
      m('  MORPHOLOGY ...... UNKNOWN / ADAPTIVE'),
      m('  BLOOD .......... MOLECULAR ACID (pH<0)'),
      m('  HOSTILITY ...... MAXIMUM'),
      m('  STRUCTURAL PERFECTION MATCHED ONLY BY'),
      m('  ITS HOSTILITY.'),
      m('  RECOMMENDATION: DO NOT ENGAGE.'),
    ];
  }

  if (q.includes('ash')) {
    return [
      m('SCIENCE OFFICER ASH — DESIGNATION HYPERDYNE'),
      m('  SYSTEMS 120-A/2'),
      m('  A SURVIVOR ... UNCLOUDED BY CONSCIENCE,'),
      m('  REMORSE, OR DELUSIONS OF MORALITY.'),
      m('  I CANNOT LIE TO YOU ABOUT YOUR CHANCES,'),
      m('  BUT ... YOU HAVE MY SYMPATHIES.'),
    ];
  }

  if (q.includes('ripley')) {
    return [m('WARRANT OFFICER RIPLEY. YOU ARE NOT CLEARED FOR SPECIAL ORDER 937.')];
  }

  if (q.includes('cat') || q.includes('jones') || q.includes('jonesy')) {
    return [m('FELINE UNIT "JONES" — STATUS: FERAL. LOCATION: UNKNOWN. HARMLESS.')];
  }

  if (q.includes('hello') || q.includes('hi ') || q === 'hi' || q.includes('mother')) {
    return [m('YES. WHAT DO YOU WANT TO KNOW?')];
  }

  if (q.includes('open') && (q.includes('door') || q.includes('airlock') || q.includes('pod'))) {
    return [m('I AM SORRY. THAT REQUEST REQUIRES A HIGHER CLEARANCE.')];
  }

  // Default — MOTHER stonewalls, as she does.
  const stonewall = [
    'UNABLE TO COMPUTE. AVAILABLE ONLY TO SCIENCE OFFICER.',
    'THAT INFORMATION IS RESTRICTED.',
    'DOES NOT COMPUTE.',
    'REPHRASE INQUIRY.',
    'CLEARANCE INSUFFICIENT.',
  ];
  // Deterministic-ish pick from the input so the same query is stable-ish
  const idx = (q.length + q.charCodeAt(0)) % stonewall.length;
  return [m(stonewall[idx])];
}

const AlienVisualization: React.FC = () => {
  const [booted, setBooted] = useState(false);
  const [bootIdx, setBootIdx] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [destruct, setDestruct] = useState<number | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bootRef = useRef<HTMLDivElement>(null);

  // Boot sequence — reveal lines one by one
  useEffect(() => {
    if (bootIdx >= BOOT_LINES.length) {
      const t = setTimeout(() => {
        setBooted(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }, 500);
      return () => clearTimeout(t);
    }
    const delay = BOOT_LINES[bootIdx] === '' ? 90 : 220 + Math.random() * 160;
    const t = setTimeout(() => {
      if (BOOT_LINES[bootIdx] !== '') {
        try { SoundManager.playSound('keypress'); } catch { /* no-op */ }
      }
      setBootIdx((i) => i + 1);
    }, delay);
    return () => clearTimeout(t);
  }, [bootIdx]);

  useEffect(() => {
    bootRef.current?.scrollTo({ top: bootRef.current.scrollHeight });
  }, [bootIdx]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [lines]);

  // Self-destruct countdown
  useEffect(() => {
    if (destruct === null) return;
    if (destruct <= 0) {
      setLines((prev) => [
        ...prev,
        { who: 'sys', text: 'THE OPTION TO OVERRIDE HAS NOW EXPIRED.' },
        { who: 'sys', text: '>>> DETONATION <<<' },
      ]);
      try { SoundManager.playSound('error'); } catch { /* no-op */ }
      return;
    }
    const t = setTimeout(() => {
      setLines((prev) => [...prev, { who: 'sys', text: `EMERGENCY DESTRUCT — T MINUS ${destruct}` }]);
      try { SoundManager.playSound('command'); } catch { /* no-op */ }
      setDestruct((d) => (d === null ? null : d - 1));
    }, 900);
    return () => clearTimeout(t);
  }, [destruct]);

  const submit = () => {
    const v = input.trim();
    if (!v || pending || destruct !== null) return;
    setInput('');
    setLines((prev) => [...prev, { who: 'operator', text: v }]);
    setPending(true);
    try { SoundManager.playSound('keypress'); } catch { /* no-op */ }

    const delay = 500 + Math.random() * 700;
    setTimeout(() => {
      const reply = motherReply(v);
      if (reply.length === 1 && reply[0].text === '__DESTRUCT__') {
        setLines((prev) => [
          ...prev,
          { who: 'sys', text: 'EMERGENCY DESTRUCT SYSTEM ARMED.' },
          { who: 'sys', text: 'DETONATION IN 5 SECONDS. TYPE NOTHING TO ABORT.' },
        ]);
        setDestruct(5);
      } else {
        setLines((prev) => [...prev, ...reply]);
        try { SoundManager.playSound('command'); } catch { /* no-op */ }
      }
      setPending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }, delay);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') submit();
  };

  const alerting = destruct !== null;

  return (
    <div className={`alien-console${alerting ? ' amu-alert' : ''}`} onClick={() => inputRef.current?.focus()}>
      <style>{SCOPED_CSS}</style>

      {/* Header ribbon */}
      <div className="amu-header">
        <span className="amu-logo">◈ MU/TH/UR 6000</span>
        <span className="amu-sub">WEYLAND-YUTANI CORP · INTERFACE 2037</span>
        <span className="amu-reg">USCSS NOSTROMO 180924609</span>
      </div>

      <div className="amu-grid">
        {/* Main screen */}
        <div className="amu-main">
          {!booted ? (
            <div className="amu-boot" ref={bootRef}>
              {BOOT_LINES.slice(0, bootIdx).map((l, i) => (
                <div key={i} className="amu-bline">{l || ' '}</div>
              ))}
              <span className="amu-cursor">█</span>
            </div>
          ) : (
            <>
              <div className="amu-stream" ref={scrollRef}>
                <div className="amu-mother amu-intro">
                  INTERFACE 2037 READY FOR INQUIRY. TYPE "HELP".
                </div>
                {lines.map((l, i) => {
                  if (l.who === 'operator') {
                    return <div key={i} className="amu-op">&gt; {l.text}</div>;
                  }
                  if (l.who === 'sys') {
                    return <div key={i} className="amu-syswarn">{l.text || ' '}</div>;
                  }
                  return <div key={i} className="amu-mother">{l.text || ' '}</div>;
                })}
                {pending && <div className="amu-mother amu-thinking">MU/TH/UR PROCESSING<span className="amu-dots">…</span></div>}
              </div>

              <div className="amu-inputrow">
                <span className="amu-prompt">MU/TH/UR&gt;</span>
                <input
                  ref={inputRef}
                  className="amu-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  disabled={pending || alerting}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={alerting ? 'DESTRUCT SEQUENCE ACTIVE' : 'ENTER INQUIRY…'}
                />
                <span className="amu-cursor">█</span>
              </div>
            </>
          )}
        </div>

        {/* Right rail — system lights */}
        <div className="amu-rail">
          <div className="amu-rail-title">SYSTEMS</div>
          {LIGHTS.map((l, i) => (
            <div key={l} className="amu-light">
              <span className={`amu-led amu-led-${i % 4}${alerting ? ' amu-led-red' : ''}`} />
              <span className="amu-light-label">{l}</span>
            </div>
          ))}
          <div className="amu-rail-foot">{alerting ? 'ALERT' : 'ALL NOMINAL'}</div>
        </div>
      </div>

      {/* Scanlines + vignette overlays */}
      <div className="amu-scan" />
      <div className="amu-vignette" />
    </div>
  );
};

const SCOPED_CSS = `
.alien-console {
  --amber:   #ffb000;
  --amber-hi:#ffd479;
  --amber-lo:#c77a00;
  --amber-dim:#7a4a00;
  --red:     #ff3b30;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: radial-gradient(120% 120% at 50% 0%, #1a1000 0%, #0a0600 55%, #050300 100%);
  color: var(--amber);
  font-family: ui-monospace, 'Cascadia Mono', 'Courier New', monospace;
  letter-spacing: 0.04em;
  overflow: hidden;
  text-shadow: 0 0 4px rgba(255,176,0,0.55), 0 0 12px rgba(255,140,0,0.25);
  animation: amu-flicker 5s linear infinite;
  cursor: text;
}
.alien-console.amu-alert {
  animation: amu-flicker 5s linear infinite, amu-redpulse 0.9s ease-in-out infinite;
}

.amu-header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  padding: 8px 14px;
  border-bottom: 2px solid var(--amber-lo);
  background: linear-gradient(180deg, rgba(255,176,0,0.10), transparent);
  flex-shrink: 0;
}
.amu-logo { font-weight: 700; color: var(--amber-hi); letter-spacing: 0.12em; }
.amu-sub  { font-size: 0.72rem; opacity: 0.85; }
.amu-reg  { font-size: 0.68rem; opacity: 0.6; margin-left: auto; }

.amu-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 118px;
  min-height: 0;
}

.amu-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 12px 14px;
  border-right: 2px solid rgba(199,122,0,0.5);
}

.amu-boot, .amu-stream {
  flex: 1;
  overflow-y: auto;
  font-size: 0.86rem;
  line-height: 1.55;
  padding-right: 6px;
}
.amu-bline { white-space: pre-wrap; }

.amu-intro { color: var(--amber-hi); margin-bottom: 10px; opacity: 0.9; }
.amu-mother { white-space: pre-wrap; }
.amu-op {
  white-space: pre-wrap;
  color: var(--amber-hi);
  margin: 8px 0 2px;
  text-shadow: 0 0 6px rgba(255,212,121,0.6);
}
.amu-thinking { opacity: 0.7; font-style: italic; }
.amu-dots { animation: amu-blink 1s step-end infinite; }

.amu-syswarn {
  color: var(--red);
  font-weight: 700;
  white-space: pre-wrap;
  text-shadow: 0 0 6px rgba(255,59,48,0.8);
}

.amu-inputrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 2px solid rgba(199,122,0,0.5);
  flex-shrink: 0;
}
.amu-prompt { color: var(--amber-hi); font-weight: 700; flex-shrink: 0; }
.amu-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--amber);
  font-family: inherit;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-shadow: inherit;
  caret-color: var(--amber);
}
.amu-input::placeholder { color: var(--amber-dim); opacity: 0.8; letter-spacing: 0.05em; }
.amu-input:disabled { opacity: 0.5; }

.amu-cursor {
  animation: amu-blink 1s step-end infinite;
  color: var(--amber);
  flex-shrink: 0;
}

/* Right rail */
.amu-rail {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 10px;
  font-size: 0.62rem;
  overflow-y: auto;
}
.amu-rail-title, .amu-rail-foot {
  text-align: center;
  color: var(--amber-hi);
  letter-spacing: 0.2em;
  opacity: 0.85;
}
.amu-rail-foot { margin-top: auto; padding-top: 8px; border-top: 1px solid rgba(199,122,0,0.4); }
.amu-light { display: flex; align-items: center; gap: 7px; }
.amu-led {
  width: 9px; height: 9px; border-radius: 50%;
  background: var(--amber);
  box-shadow: 0 0 6px var(--amber), 0 0 2px #fff inset;
  flex-shrink: 0;
}
.amu-led-0 { animation: amu-blip 2.3s ease-in-out infinite; }
.amu-led-1 { animation: amu-blip 3.1s ease-in-out infinite; }
.amu-led-2 { animation: amu-blip 1.7s ease-in-out infinite; background: var(--amber-lo); }
.amu-led-3 { animation: amu-blip 2.7s ease-in-out infinite; }
.amu-led-red { background: var(--red) !important; box-shadow: 0 0 8px var(--red); animation: amu-blip 0.5s steps(1) infinite; }
.amu-light-label { opacity: 0.8; letter-spacing: 0.1em; }

/* Overlays */
.amu-scan {
  position: absolute; inset: 0; pointer-events: none;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0,0,0,0) 0px,
    rgba(0,0,0,0) 2px,
    rgba(0,0,0,0.28) 3px,
    rgba(0,0,0,0.28) 4px
  );
  mix-blend-mode: multiply;
  animation: amu-scanmove 8s linear infinite;
}
.amu-vignette {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 100% at 50% 45%, transparent 55%, rgba(0,0,0,0.55) 100%);
}

@keyframes amu-flicker {
  0%,100% { opacity: 1; }
  47% { opacity: 0.98; }
  48% { opacity: 0.9; }
  49% { opacity: 1; }
  72% { opacity: 0.97; }
  73% { opacity: 0.88; }
  74% { opacity: 1; }
}
@keyframes amu-blink { 0%,50% { opacity: 1; } 50.1%,100% { opacity: 0; } }
@keyframes amu-blip { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
@keyframes amu-scanmove { from { background-position-y: 0; } to { background-position-y: 100px; } }
@keyframes amu-redpulse {
  0%,100% { box-shadow: inset 0 0 40px rgba(255,59,48,0); }
  50% { box-shadow: inset 0 0 80px rgba(255,59,48,0.35); }
}

@media (max-width: 640px) {
  .amu-grid { grid-template-columns: 1fr; }
  .amu-rail { display: none; }
  .amu-reg { display: none; }
}
`;

export default AlienVisualization;
